import useLookupListData from "@app/data-hooks/use-lookup-list-data.ts";
import useStaffData from "@app/data-hooks/use-staff-data.ts";
import { Checkbox, FormControlLabel, Grid2, Stack, TextField } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { QueryStatusRouter, Select } from "bi-lib";
import { useEffect, useRef } from "react";

interface Props {
    id?: number;
    setOnSave: (onSave: () => Promise<unknown>) => void;
}

export default function ContactForm({ id, setOnSave }: Props) {
    const { staff, saveContact, companyList } = useStaffData({ id });
    const { membershipRolesList, stateCodeList } = useLookupListData();

    const form = useForm({
        defaultValues: {
            roleId: staff.data?.roleId,
            email: staff.data?.email,
            firstName: staff.data?.firstName,
            middleInitial: staff.data?.middleInitial,
            lastName: staff.data?.lastName,
            title: staff.data?.title,
            staffCompanyId: staff.data?.staffCompanyId,
            initials: staff.data?.initials,
            isActive: staff.data?.isActive,
            isHourly: staff.data?.isHourly,
            address1: staff.data?.address?.address1,
            address2: staff.data?.address?.address2,
            cityStateId: staff.data?.address?.cityStateId,
            city: staff.data?.address?.city,
            stateCodeId: staff.data?.address?.stateCodeId,
            state: staff.data?.address?.state,
            postalCode: staff.data?.address?.postalCode,
            homePhone: staff.data?.homePhone,
            homeFax: staff.data?.homeFax,
            workPhone: staff.data?.workPhone,
            workFax: staff.data?.workFax,
        },
        onSubmit: async ({ value }) =>
            await saveContact.mutateAsync({
                staffingId: id,
                roleId: value.roleId,
                email: value.email,
                firstName: value.firstName,
                middleInitial: value.middleInitial,
                lastName: value.lastName,
                title: value.title,
                staffCompanyId: value.staffCompanyId,
                initials: value.initials,
                isActive: value.isActive,
                isHourly: value.isHourly,
                address: {
                    address1: value.address1,
                    address2: value.address2,
                    cityStateId: value.cityStateId,
                    city: value.city,
                    stateCodeId: value.stateCodeId,
                    state: value.state,
                    postalCode: value.postalCode,
                },
                homePhone: value.homePhone,
                homeFax: value.homeFax,
                workPhone: value.workPhone,
                workFax: value.workFax,
            }),
    });

    const ref = useRef<HTMLFormElement>(null);

    const handleSave = async () => {
        if (ref.current?.reportValidity()) {
            await form.handleSubmit();
            return;
        }
        throw new Error("Form is invalid");
    };

    useEffect(() => {
        setOnSave(handleSave);
    }, [form]);

    return (
        <form
            ref={ref}
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit();
            }}
        >
            <QueryStatusRouter queryHook={staff}>
                <Grid2 container spacing={1} mt={1}>
                    <Grid2 size={12}>
                        <QueryStatusRouter queryHook={membershipRolesList}>
                            <form.Field name={"roleId"}>
                                {(field) => (
                                    <Select
                                        value={field.state.value ?? undefined}
                                        onChange={(e) => field.handleChange(e)}
                                        label={"Role"}
                                        required
                                        fullWidth
                                        options={membershipRolesList.data?.map((x) => ({
                                            label: x.roleName,
                                            value: x.roleId ?? undefined,
                                        }))}
                                    />
                                )}
                            </form.Field>
                        </QueryStatusRouter>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"firstName"}>
                            {(field) => (
                                <TextField
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    label={"First Name"}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={2}>
                        <form.Field name={"middleInitial"}>
                            {(field) => (
                                <TextField
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    label={"Middle Initial"}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"lastName"}>
                            {(field) => (
                                <TextField
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    label={"Last Name"}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={2}>
                        <form.Field name={"initials"}>
                            {(field) => (
                                <TextField
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    label={"Initials"}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"email"}>
                            {(field) => (
                                <TextField
                                    type={"email"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    label={"Email"}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <QueryStatusRouter queryHook={companyList}>
                            <form.Field name={"staffCompanyId"}>
                                {(field) => (
                                    <Select
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e)}
                                        label={"Company"}
                                        fullWidth
                                        options={companyList.data?.map((x) => ({
                                            label: x.companyName,
                                            value: x.staffCompanyId,
                                        }))}
                                        required
                                    />
                                )}
                            </form.Field>
                        </QueryStatusRouter>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"title"}>
                            {(field) => (
                                <TextField
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    label={"Title"}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <Stack spacing={1}>
                            <form.Field name={"address1"}>
                                {(field) => (
                                    <TextField
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        label={"Address 1"}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                            <form.Field name={"address2"}>
                                {(field) => (
                                    <TextField
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        label={"Address 2"}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                            <form.Field name={"city"}>
                                {(field) => (
                                    <TextField
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        label={"City"}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </Stack>
                    </Grid2>
                    <Grid2 size={6}>
                        <Stack spacing={1}>
                            <QueryStatusRouter queryHook={stateCodeList}>
                                <form.Field name={"stateCodeId"}>
                                    {(field) => (
                                        <Select
                                            value={field.state.value}
                                            onChange={(e) => field.handleChange(e)}
                                            label={"State"}
                                            fullWidth
                                            options={stateCodeList.data?.map((x) => ({
                                                label: x.stateName,
                                                value: x.stateCodeId,
                                            }))}
                                        />
                                    )}
                                </form.Field>
                            </QueryStatusRouter>
                            <form.Field name={"postalCode"}>
                                {(field) => (
                                    <TextField
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        label={"Postal code"}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </Stack>
                    </Grid2>
                    <Grid2 size={6}>
                        <Stack spacing={1}>
                            <form.Field name={"homePhone"}>
                                {(field) => (
                                    <TextField
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        label={"Home Phone"}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                            <form.Field name={"workPhone"}>
                                {(field) => (
                                    <TextField
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        label={"Work Phone"}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </Stack>
                    </Grid2>
                    <Grid2 size={6}>
                        <Stack spacing={1}>
                            <form.Field name={"homeFax"}>
                                {(field) => (
                                    <TextField
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        label={"Home Fax"}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                            <form.Field name={"workFax"}>
                                {(field) => (
                                    <TextField
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        label={"Work Fax"}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </Stack>
                    </Grid2>
                </Grid2>
                <Stack direction={"row"} spacing={1}>
                    <form.Field name={"isActive"}>
                        {(field) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.state.value ?? false}
                                        onChange={(e) => field.handleChange(e.target.checked)}
                                    />
                                }
                                label={"Active"}
                            />
                        )}
                    </form.Field>
                    <form.Field name={"isHourly"}>
                        {(field) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.state.value ?? false}
                                        onChange={(e) => field.handleChange(e.target.checked)}
                                    />
                                }
                                label={"Is Hourly"}
                            />
                        )}
                    </form.Field>
                </Stack>
            </QueryStatusRouter>
        </form>
    );
}
